/**
 * useTabChart - Adapted version of useSimplifiedChart for tab system
 * 
 * Provides per-tab chart instances with isolated rendering and event handling
 * while maintaining the proven architecture from commit e80c574d
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { useEffect, useState, useCallback, useRef } from 'react';
import { TabChartRenderer } from './TabChartRenderer';
import { TabChartEventManager } from './TabChartEventManager';
import { UniversalChakraRendererManager } from './UniversalChakraRendererManager';
import { globalEventStream } from '../core/EventStream';
import { initializeEnhancedAnimationConsole } from '../testing/EnhancedAnimationConsole';
import { ApiMapper } from '../../../../../utils/ApiMapper';
import { JatakaApiResponse } from '../../../../../types/JatakaApiTypes';

export interface TabChartOptions {
    chartId: string;
    debugMode?: boolean;
    enableAnimations?: boolean;
    initialDisplayMode?: 'symbols' | 'short';
}

export interface TabChartState {
    isInitialized: boolean;
    isRendering: boolean;
    lastRenderTime: number;
    elementsRendered: number;
    error: string | null;
}

/**
 * Tab-aware chart hook - manages per-tab chart instances
 */
export function useTabChart(options: TabChartOptions) {
    const [state, setState] = useState<TabChartState>({
        isInitialized: false,
        isRendering: false,
        lastRenderTime: 0,
        elementsRendered: 0,
        error: null
    });
    
    const rendererRef = useRef<TabChartRenderer | null>(null);
    const eventManagerRef = useRef<TabChartEventManager | null>(null);
    const chakraManagerRef = useRef<UniversalChakraRendererManager | null>(null);
    const { chartId } = options;
    
    /**
     * Initialize tab chart system with chart ID namespace
     */
    const initializeChart = useCallback(() => {
        try {
            if (rendererRef.current || eventManagerRef.current) {
                // Already initialized
                return;
            }
            
            const debugMode = options.debugMode || false;
            
            if (debugMode) {
                devLog(`🚀 useTabChart[${chartId}]: Initializing chart system`);
            }

            // Create renderer with chart ID for namespace isolation
            const renderer = new TabChartRenderer(globalEventStream, debugMode, chartId);

            // Create event manager with chart-specific event handling
            const eventManager = new TabChartEventManager(renderer, globalEventStream, debugMode, chartId);

            rendererRef.current = renderer;
            eventManagerRef.current = eventManager;

            // Initialize UniversalChakraRendererManager (singleton - once per app)
            if (typeof window !== 'undefined' && !window.universalChakraRendererManager) {
                const chakraManager = new UniversalChakraRendererManager(globalEventStream);
                chakraManagerRef.current = chakraManager;
                window.universalChakraRendererManager = chakraManager;

                if (debugMode) {
                    devLog('🎨 UniversalChakraRendererManager: Initialized globally');
                }
            } else {
                // Use existing singleton
                chakraManagerRef.current = window.universalChakraRendererManager as UniversalChakraRendererManager;
            }

            // Export to window with chart ID namespace
            if (typeof window !== 'undefined') {
                if (!window.chartRenderers) {
                    window.chartRenderers = {};
                }
                window.chartRenderers[chartId] = renderer;
            }

            setState(prev => ({
                ...prev,
                isInitialized: true,
                error: null
            }));

            // Initialize Enhanced Animation Console (shared)
            if (typeof window !== 'undefined' && !window.enhancedAnimationConsoleInitialized) {
                initializeEnhancedAnimationConsole();
                window.enhancedAnimationConsoleInitialized = true;
            }

            if (debugMode) {
                devLog(`✅ useTabChart[${chartId}]: Chart system initialized`);
            }
            
        } catch (error) {
            devError(`❌ useTabChart[${chartId}]: Initialization failed:`, error);
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'Unknown error',
                isInitialized: false
            }));
        }
    }, [options.debugMode, chartId]);
    
    /**
     * Cleanup tab chart resources
     */
    const cleanup = useCallback(() => {
        if (eventManagerRef.current) {
            eventManagerRef.current.cleanup();
            eventManagerRef.current = null;
        }
        
        if (rendererRef.current) {
            rendererRef.current.cleanup();  
            rendererRef.current = null;
        }
        
        // Remove from window
        if (typeof window !== 'undefined' && window.chartRenderers) {
            delete window.chartRenderers[chartId];
        }

        setState(prev => ({
            ...prev,
            isInitialized: false
        }));

        if (options.debugMode) {
            devLog(`🧹 useTabChart[${chartId}]: Cleanup completed`);
        }
    }, [options.debugMode, chartId]);
    
    /**
     * Force render with JatakaApiResponse - converts to ReadyChartData
     */
    const forceRender = useCallback(async (apiResponse: JatakaApiResponse) => {
        if (!eventManagerRef.current) {
            devWarn(`⚠️ useTabChart[${chartId}]: Event manager not initialized`);
            return;
        }

        setState(prev => ({ ...prev, isRendering: true, error: null }));

        try {
            // Convert JatakaApiResponse to ReadyChartData
            const readyChartData = ApiMapper.toReadyChartData(apiResponse, chartId, 'force-render');
            await eventManagerRef.current.forceRender(readyChartData);
            
            setState(prev => ({
                ...prev,
                isRendering: false,
                lastRenderTime: Date.now()
            }));
            
        } catch (error) {
            devError(`❌ useTabChart[${chartId}]: Force render failed:`, error);
            setState(prev => ({
                ...prev,
                isRendering: false,
                error: error instanceof Error ? error.message : 'Render failed'
            }));
        }
    }, [chartId]);
    
    /**
     * Switch display mode for this chart tab
     */
    const switchDisplayMode = useCallback(async (mode: 'symbols' | 'short') => {
        if (!rendererRef.current) {
            devWarn(`⚠️ useTabChart[${chartId}]: Renderer not initialized`);
            return;
        }

        try {
            await rendererRef.current.switchDisplayMode(mode);

            if (options.debugMode) {
                devLog(`🔄 useTabChart[${chartId}]: Switched to ${mode} mode`);
            }

        } catch (error) {
            devError(`❌ useTabChart[${chartId}]: Mode switch failed:`, error);
        }
    }, [options.debugMode, chartId]);
    
    /**
     * Get statistics for this chart tab
     */
    const getStats = useCallback(() => {
        if (!rendererRef.current || !eventManagerRef.current) {
            return null;
        }
        
        return {
            chartId,
            renderer: rendererRef.current.getStats(),
            eventManager: eventManagerRef.current.getStats(),
            hookState: state
        };
    }, [state, chartId]);
    
    /**
     * Initialize on mount
     */
    useEffect(() => {
        initializeChart();
        
        return () => {
            cleanup();
        };
    }, [initializeChart, cleanup]);
    
    /**
     * Set initial display mode
     */
    useEffect(() => {
        if (state.isInitialized && options.initialDisplayMode) {
            switchDisplayMode(options.initialDisplayMode);
        }
    }, [state.isInitialized, options.initialDisplayMode, switchDisplayMode]);
    
    return {
        // State
        ...state,
        chartId,
        
        // Methods
        forceRender,
        switchDisplayMode,
        getStats,
        cleanup,
        
        // Refs for direct access
        renderer: rendererRef.current,
        eventManager: eventManagerRef.current
    };
}

/**
 * Debug version with console exports
 */
export function useTabChartDebug(options: TabChartOptions) {
    const chart = useTabChart({ ...options, debugMode: true });

    // Add to window for debugging
    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (!window.tabCharts) {
                window.tabCharts = {};
            }
            window.tabCharts[options.chartId] = chart;
        }

        return () => {
            if (typeof window !== 'undefined' && window.tabCharts) {
                delete window.tabCharts[options.chartId];
            }
        };
    }, [chart, options.chartId]);

    return chart;
}